import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { MRT_ColumnDef } from "material-react-table";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import CustomTable from "../../../../../shared/components/table/custom-table";
import ActionsModalPopup, {
  ActionButtonConfig,
} from "../../../../../shared/components/modals/action-popup";
import CustomStepper from "../../../../../shared/components/stepper/steppers";
import useNotification from "../../../../../shared/hooks/useNotification";
import AlertPopup from "../../../../../shared/components/modals/alert-moadal";
import ComponentInfo from "../component-details/component-details/component-details-form";
import ProductComponent from "../component-details/component-details/product-components";
import RawMaterialInfo from "../component-details/component-details/raw-material-info";
import {
  TCComponentInformation,
  TCProductComponentStandardInformation,
  TCComponentRawMaterialInformation,
} from "../../../tc-models/tc-req-dto/tc-component-details.req_dto";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { isViewOnlyAdminMode } from "../../../../../shared/utils/admin-utils";
import { useApi } from "../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import { DELETECONFIG, TCAppConfig } from "../../../../../shared/app-configs/tc-app-configs";
import { ActionsMenu } from "../../../../../shared/components/table/custom-actions";
import {
  fetchaddComponentDetails,
  mapComponentData,
} from "../component-details/component-details-apis";
import {
  mapToApiComponentData,
  type Component as ApiComponent,
  getProductShipmentDataByProductNo,
} from "../component-details/component-details.model";
import { Pagination } from "../../../../../shared/hooks/useRecordStore";

// Minimal shape of a product row from GET_PRODUCT_RAWMATERIAL_INFO_LIST
interface ProductListRow {
  tcProductNo?: string;
  tcProductShipmentNo?: string | string[];
  tcAssociatedProductShipmentNo?: string | string[];
  productId?: string;
  shipmentId?: string;
  [key: string]: any;
}

export interface ComponentEditContext {
  componentId: string;
  productId: string;
  shipmentId: string;
  productNumber: string;
  shipmentNumber: string;
}

interface ComponentCreationModalProps {
  open: boolean;
  onClose: () => void;
  shipmentNumber: string;
  productNumber: string;
  tcId?: string;
  productId?: string;
  shipmentId?: string;
  /** Product list from GET_PRODUCT_RAWMATERIAL_INFO_LIST – used to resolve productId/shipmentId for new components */
  productList?: ProductListRow[];
  /** When set, modal opens in edit mode: fetches component details and shows the stepper drawer */
  editContext?: ComponentEditContext | null;
  onAddComponents?: () => void;
  /** Called after a component is saved or deleted so parent can refetch product detail (totals/RM composition) */
  onComponentSaved?: () => void | Promise<void>;
  onContinue?: (components: ComponentData[]) => void;
  /** Called when the user cancels a newly created product (via header close) so parent can reset product state */
  onProductDeleted?: () => void;
}

interface ComponentData {
  componentNumber: string;
  componentDetail: string;
  componentNetShippingWeight: number;
  componentSupplementaryWeight: number;
  componentCertifiedWeight: number;
  componentCertifiedAndNonCertifiedWeight: number | null;
  rawMaterialInfo?: TCComponentRawMaterialInformation[];
  standardInfo?: TCProductComponentStandardInformation[];
  componentId?: string;
  productId?: string;
  shipmentId?: string;
}

// Initial state for component info
const initComponentInfo: TCComponentInformation = {
tcAssociatedProductNo:"",
  tcProductComponentOrder: "",
  tcProductComponentNo: "",
  tcComponentDetail: "",
  tcComponentInfoTrimsExcluded: "",
  tcComponentInfoAssociatedTc: "",
  tcComponentInfoFiberLength: "",
  tcComponentInfoYarnCount: "",
  tcComponentInfoFabricWeightAndConstruct: "",
  tcComponentInfoFabricWeightOfFinishedProduct: "",
  tcComponentNetShippingWeight: null,
  tcComponentSupplementaryWeight: null,
  tcComponentCertifiedAndNonCertifiedWeight: null,
  tcComponentInfoAdditional: "",
  tcComponentCertifiedWeightTotal: 0,
  tcComponentNonCertifiedWeightTotal: 0,
};

const initStandardInfo: TCProductComponentStandardInformation[] = [];
const initRawMaterialInfo: TCComponentRawMaterialInformation[] = [];

function mapApiRowToComponentData(row: ApiComponent): ComponentData {
  return {
    componentNumber: row.tcProductComponentNo ?? "",
    componentDetail: row.tcComponentDetail ?? "",
    componentNetShippingWeight: Number(row.tcComponentNetShippingWeight) || 0,
    componentSupplementaryWeight: Number(row.tcComponentSupplementaryWeight) || 0,
    componentCertifiedWeight: Number(row.tcComponentCertifiedWeightTotal) || 0,
    componentCertifiedAndNonCertifiedWeight: Number(row.tcComponentNonCertifiedWeightTotal) || 0,
    componentId: row.componentId,
    productId: row.productId,
    shipmentId: row.shipmentId,
  };
}

const ComponentCreationModal: React.FC<ComponentCreationModalProps> = ({
  open,
  onClose,
  shipmentNumber = "",
  productNumber = "",
  tcId = "",
  productId: propProductId = "",
  shipmentId: propShipmentId = "",
  productList = [],
  editContext = null,
  onAddComponents,
  onComponentSaved,
  onContinue,
  onProductDeleted,
}) => {
  const [components, setComponents] = useState<ComponentData[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingComponentIndex, setEditingComponentIndex] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<ComponentData | null>(null);
  const editContextHandledRef = useRef(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<ComponentData | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState<number>(0);
  const { notify } = useNotification();
  const componentInfoRef = useRef<any>(null);
  const { loading, executeGet, setLoadingState, executePost, executeDelete } = useApi();
  const { executeGet: executeAddGet } = useApi();

  const [stepData, setStepData] = useState({
    componentInfo: initComponentInfo,
    standardInfo: initStandardInfo,
    rawMaterialInfo: initRawMaterialInfo,
  });
  const { isRestrictPopUpSave } = useTransactionCertificateStore();
  // Admin is view-only when viewing another CB; edit allowed for own CB
  const isViewMode = isViewOnlyAdminMode();

  // Helper function to round values to maximum 5 decimals (0-5 decimals dynamically)
  const toFiveDecimals = (value: number): number => {
    if (Number.isNaN(value)) return 0;
    return Number(value.toFixed(5));
  };

  const fetchComponentList = async () => {
    // Require tcId, shipmentId AND productId to avoid calling the API
    // with an empty/undefined productId (e.g. after delete via header close).
    if (!tcId || !propShipmentId || !propProductId) return;
    try {
      setLoadingState(true);
      const response: any = await executeGet(TCAppConfig.TC_COMP_PROD_API, {
        tcId,
        shipmentId: propShipmentId,
        productId: propProductId,
      });
      const result = response?.data;
      const list: ApiComponent[] = result?.response ?? [];
      setComponents(list.map(mapApiRowToComponentData));
      setRowCount(response?.data?.no_of_records ?? 0);
    } catch (err) {
      console.error("Failed to fetch component list:", err);
      notify("Failed to load components", "error");
      setComponents([]);
      setRowCount(0);
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    if (!open) {
      editContextHandledRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && tcId && propShipmentId) {
      fetchComponentList();
    }
  }, [open, tcId, propShipmentId, propProductId]);

  useEffect(() => {
    if (!open || !editContext?.componentId || !tcId || editContextHandledRef.current) return;
    editContextHandledRef.current = true;
    setLoadingState(true);
    fetchaddComponentDetails(
      executeAddGet,
      tcId,
      editContext.shipmentId,
      editContext.productId,
      editContext.componentId
    )
      .then((data) => {
        const productList = data?.data?.tcComponentInformation;
        if (!productList || productList.length === 0) {
          notify("No component details found", "error");
          setLoadingState(false);
          return;
        }
        const mappedStepData = mapToApiComponentData(productList[0]);
        setStepData(mappedStepData);
        setEditingRow({
          componentNumber: "",
          componentDetail: "",
          componentNetShippingWeight: 0,
          componentSupplementaryWeight: 0,
          componentCertifiedWeight: 0,
          componentCertifiedAndNonCertifiedWeight: null,
          componentId: editContext.componentId,
          productId: editContext.productId,
          shipmentId: editContext.shipmentId,
        });
        setEditingComponentIndex(0);
        setDrawerOpen(true);
        setLoadingState(false);
      })
      .catch((err) => {
        console.error("Error fetching component details:", err);
        notify(err?.message ?? "Failed to load component details", "error");
        setLoadingState(false);
      });
  }, [open, editContext, tcId, executeAddGet, notify]);

  const totalNetShippingWeight = toFiveDecimals(
    components.reduce(
      (sum, comp) => sum + (Number(comp.componentNetShippingWeight) || 0),
      0
    )
  );
  const totalSupplementaryWeight = toFiveDecimals(
    components.reduce(
      (sum, comp) => sum + (Number(comp.componentSupplementaryWeight) || 0),
      0
    )
  );
  const totalCertifiedWeight = toFiveDecimals(
    components.reduce(
      (sum, comp) => sum + (Number(comp.componentCertifiedWeight) || 0),
      0
    )
  );
  const totalNonCertifiedWeight = toFiveDecimals(
    components.reduce(
      (sum, comp) =>
        sum + (Number(comp.componentCertifiedAndNonCertifiedWeight) || 0),
      0
    )
  );

  const handleDataUpdate = (stepKey: string, updatedData: any) => {
    setStepData((prev) => ({
      ...prev,
      [stepKey]: updatedData,
    }));
  };

  const toggleDrawer = () => {
    setActiveStep(0);
    if (drawerOpen) {
      setStepData({
        componentInfo: { ...initComponentInfo, tcAssociatedProductNo: productNumber },
        standardInfo: initStandardInfo,
        rawMaterialInfo: initRawMaterialInfo,
      });
      setEditingComponentIndex(null);
      setEditingRow(null);
    } else {
      setStepData((prev) => ({
        ...prev,
        componentInfo: { ...prev.componentInfo, tcAssociatedProductNo: productNumber },
      }));
    }
    setDrawerOpen((prev) => !prev);
  };

  const handleEditComponent = (comp: ComponentData) => {
    setEditingRow(comp);
    setEditingComponentIndex(0);
    setLoadingState(true);
    fetchaddComponentDetails(
      executeAddGet,
      tcId,
      comp.shipmentId || "",
      comp.productId || "",
      comp.componentId || ""
    )
      .then((data) => {
        const productList = data?.data?.tcComponentInformation;
        if (!productList || productList.length === 0) {
          notify("No component details found", "error");
          setLoadingState(false);
          return;
        }
        const mappedStepData = mapToApiComponentData(productList[0]);
        setStepData(mappedStepData);
        setDrawerOpen(true);
        setLoadingState(false);
      })
      .catch((err) => {
        console.error("Error fetching component details:", err);
        notify(err?.message || "Failed to load component details", "error");
        setLoadingState(false);
      });
  };

  const handleNext = async () => {
    if (activeStep === 0 && componentInfoRef?.current) {
      try {
        const errors = await componentInfoRef.current.validateForm();
        await componentInfoRef.current.submitForm();

        if (Object.keys(errors).length !== 0) {
          notify("Please fill in all required fields correctly", "error");
          return;
        }

        // Backend weight validation – mirror product-weight-validate behavior for components
        const formValues = componentInfoRef.current.values ?? {};
        const netWeight =
          Number(formValues.tcComponentNetShippingWeight ?? 0) || 0;

        if (!tcId || !netWeight) {
          // If we don't have enough context, just proceed without backend check
          setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
          return;
        }

        // Resolve productId and shipmentId similarly to handleFinish
        let resolvedProductId: string | undefined =
          (editingRow?.productId as string | undefined) || propProductId;
        let resolvedShipmentId: string | undefined =
          (editingRow?.shipmentId as string | undefined) || propShipmentId;

        if ((!resolvedProductId || !resolvedShipmentId) && productList?.length) {
          let shipmentData = undefined as
            | {
                productId: string;
                shipmentId: string[] | undefined;
                tcProductShipmentNo: string[] | undefined;
              }
            | undefined;

          if (productNumber) {
            shipmentData = getProductShipmentDataByProductNo(
              productList,
              productNumber
            ) as any;
          }

          if (!shipmentData && productList.length === 1) {
            shipmentData = {
              productId: productList[0].productId ?? "",
              shipmentId: Array.isArray(productList[0].shipmentId)
                ? (productList[0].shipmentId as string[])
                : productList[0].shipmentId
                ? [productList[0].shipmentId]
                : [],
              tcProductShipmentNo: Array.isArray(
                productList[0].tcAssociatedProductShipmentNo
              )
                ? (productList[0].tcAssociatedProductShipmentNo as string[])
                : productList[0].tcAssociatedProductShipmentNo
                ? [productList[0].tcAssociatedProductShipmentNo as string]
                : [],
            };
          }

          if (!shipmentData && productList.length > 0) {
            shipmentData = {
              productId: productList[0].productId ?? "",
              shipmentId: Array.isArray(productList[0].shipmentId)
                ? (productList[0].shipmentId as string[])
                : productList[0].shipmentId
                ? [productList[0].shipmentId]
                : [],
              tcProductShipmentNo: Array.isArray(
                productList[0].tcAssociatedProductShipmentNo
              )
                ? (productList[0].tcAssociatedProductShipmentNo as string[])
                : productList[0].tcAssociatedProductShipmentNo
                ? [productList[0].tcAssociatedProductShipmentNo as string]
                : [],
            };
          }

          if (shipmentData) {
            resolvedProductId = shipmentData.productId;
            resolvedShipmentId = Array.isArray(shipmentData.shipmentId)
              ? shipmentData.shipmentId[0]
              : shipmentData.shipmentId;
          }
        }

        if (!resolvedProductId || !resolvedShipmentId) {
          notify(
            "Unable to resolve product/shipment for component weight validation.",
            "error"
          );
          return;
        }

        const componentIdForValidation =
          editingRow?.componentId ?? editContext?.componentId ?? undefined;

        const params: Record<string, string | number> = {
          tcId,
          shipmentId: resolvedShipmentId,
          productId: resolvedProductId,
          weight: netWeight,
        };
        if (componentIdForValidation) {
          params.componentId = componentIdForValidation;
        }

        try {
          const result: any = await executeGet(
            TCAppConfig.COMPONENT_WEIGHT_VALIDATE,
            params
          );
          if (result?.status === 409) {
            // Backend will return a descriptive message which global error handler surfaces
            return;
          }
        } catch (err) {
          console.error("Component weight validate failed:", err);
          // On unexpected error, do not block the user completely – allow them to proceed
        }

        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } catch (error) {
        console.error("Form submission error:", error);
      }
    } else if (activeStep === 1) {
      const displayWeight =
        Number(stepData.componentInfo.tcComponentCertifiedAndNonCertifiedWeight) || 0;
      const rows = stepData.rawMaterialInfo || [];
      const totalPct = rows.reduce((sum, row) => {
        const p = row.tcComponentRawMaterialPercentage;
        const n = typeof p === "number" ? p : parseFloat(String(p));
        return sum + (Number.isNaN(n) ? 0 : n);
      }, 0);
      const tolerance = 1e-6;
      if (rows.length === 0) {
        notify("Please add at least one raw material row.", "error");
        return;
      }
      if (Math.abs(totalPct - 100) > tolerance) {
        const remainingPct = 100 - totalPct;
        const remainingWeight =
          displayWeight > 0
            ? Math.round((displayWeight * remainingPct) / 100 * 100) / 100
            : 0;
        notify(
          `Total raw material percentage must be 100%. Current total: ${totalPct}%. Remaining: ${remainingPct}%${displayWeight > 0 ? ` (${remainingWeight} kg)` : ""}. Please allocate the remaining percentage.`,
          "error"
        );
        return;
      }
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    } else {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleFinish = async () => {
    if (!tcId) {
      notify("TC id is required to save", "error");
      return;
    }
    const editingComp = editingRow ?? null;

    let resolvedProductId: string | undefined = editingComp?.productId ?? propProductId;
    let resolvedShipmentId: string | string[] | undefined = editingComp?.shipmentId ?? propShipmentId;
    let resolvedShipmentNo: string[] | undefined = undefined;

    // Use getProductShipmentDataByProductNo to resolve product and shipment data
    if ((!resolvedProductId || !resolvedShipmentId) && productList?.length) {
      let shipmentData = undefined;
      
      // Try exact match on productNumber first
      if (productNumber) {
        shipmentData = getProductShipmentDataByProductNo(productList, productNumber);
      }
      
      // Fallback: if no match found and only one product in list, use that product
      if (!shipmentData && productList.length === 1) {
        shipmentData = {
          productId: productList[0].productId,
          shipmentId: Array.isArray(productList[0].shipmentId) 
            ? productList[0].shipmentId 
            : productList[0].shipmentId ? [productList[0].shipmentId] : [],
          tcProductShipmentNo: Array.isArray(productList[0].tcAssociatedProductShipmentNo)
            ? productList[0].tcAssociatedProductShipmentNo
            : productList[0].tcAssociatedProductShipmentNo ? [productList[0].tcAssociatedProductShipmentNo] : [],
        };
      }
      
      // Fallback: if still no match, use first available product
      if (!shipmentData && productList.length > 0) {
        shipmentData = {
          productId: productList[0].productId,
          shipmentId: Array.isArray(productList[0].shipmentId) 
            ? productList[0].shipmentId 
            : productList[0].shipmentId ? [productList[0].shipmentId] : [],
          tcProductShipmentNo: Array.isArray(productList[0].tcAssociatedProductShipmentNo)
            ? productList[0].tcAssociatedProductShipmentNo
            : productList[0].tcAssociatedProductShipmentNo ? [productList[0].tcAssociatedProductShipmentNo] : [],
        };
      }

      if (shipmentData) {
        resolvedProductId = shipmentData.productId;
        resolvedShipmentId = shipmentData.shipmentId;
        resolvedShipmentNo = shipmentData.tcProductShipmentNo;
      }
    }

    const payloadStepData = {
      ...stepData,
      componentInfo: {
        ...stepData.componentInfo,
        tcAssociatedProductNo:
          productNumber || stepData.componentInfo.tcAssociatedProductNo,
      },
    };
    const newComponentId = editingComp?.componentId ?? null;
    const payload = mapComponentData(payloadStepData, tcId, newComponentId);

    // Close the stepper modal immediately; keep save running in background
    if (drawerOpen) {
      toggleDrawer();
    }

    setLoadingState(true);
    try {
      const shipmentIdParam = Array.isArray(resolvedShipmentId) ? resolvedShipmentId : [resolvedShipmentId];
      const queryParams = new URLSearchParams({
        tcId,
        shipmentId: JSON.stringify(shipmentIdParam),
        productId: String(resolvedProductId),
        shipmentNo: encodeURIComponent(
          resolvedShipmentNo ? JSON.stringify(resolvedShipmentNo) : JSON.stringify(shipmentNumber || "")
        ),
      }).toString();

      // For new components, use the resolved productId; for edits, use the original
      const oldProductId = editingComp?.productId ?? String(resolvedProductId);
      const oldShipmentIdArray = editingComp?.shipmentId
        ? [editingComp.shipmentId]
        : shipmentIdParam;
      const oldShipmentIdParam = JSON.stringify(oldShipmentIdArray);

      const finalUrl = `${TCAppConfig.COMPONENT_DETAILS_ENDPOINT}?${queryParams}&oldProductId=${oldProductId}&oldShipmentId=${oldShipmentIdParam}`;
      const result: any = await executePost(finalUrl, payload);
      if (result?.status === 200) {
        notify("Data Saved Successfully", "success");
        await fetchComponentList();
        await onComponentSaved?.();
      } else {
        notify("Unable to Save", "error");
      }
    } catch (err) {
      console.error("Save component failed:", err);
      notify("Unable to Save", "error");
    } finally {
      setLoadingState(false);
    }
  };

  const steps = [
    {
      label: "Component Information",
      content: (
        <ComponentInfo
          formRef={componentInfoRef}
          componentData={stepData.componentInfo}
          setComponentData={(data) => handleDataUpdate("componentInfo", data)}
        />
      ),
    },
    {
      label: "Raw Material Information",
      content: (
        <RawMaterialInfo
          certifiedAndNonCertifiedWeight={
            stepData.componentInfo.tcComponentCertifiedAndNonCertifiedWeight
          }
          rawMaterialInfoData={stepData.rawMaterialInfo}
          setRawMaterialInfoData={(data) =>
            handleDataUpdate("rawMaterialInfo", data)
          }
        />
      ),
    },
    {
      label: "Standard Information",
      content: (
        <ProductComponent
          productInfoData={stepData.standardInfo}
          setProductInfoData={(data) => handleDataUpdate("standardInfo", data)}
          certifiedAllocatedWeight={(stepData.rawMaterialInfo || []).reduce(
            (sum: number, row: any) =>
              row?.tcComponentRawMaterialCertified === "Yes"
                ? sum + (Number(row?.tcComponentRawMaterialAllocatedWeight) || 0)
                : sum,
            0
          )}
        />
      ),
    },
  ];

  const disableStepperNav =
    (activeStep === 1 || activeStep === 2) && isRestrictPopUpSave;

  const stepperActions: ActionButtonConfig[] = [
    ...activeStep !== 0
      ? [
          {
            label: "Back",
            onClick: handleBack,
            variant: "contained" as "contained",
            color: "default" as "default",
            disabled: activeStep === 0 || disableStepperNav,
          },
        ]
      : [],
    {
      label: activeStep === steps.length - 1
        ? isViewMode ? "Next" : "Save"
        : "Next",
      onClick: isViewMode
        ? activeStep === steps.length - 1
          ? toggleDrawer
          : () => setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))
        : activeStep === steps.length - 1
        ? handleFinish
        : handleNext,
      variant: "contained" as "contained",
      color: "primary" as "primary",
      disabled: !isViewMode && disableStepperNav,
    },
  ];

  const handleAddComponent = () => {
    setStepData((prev) => ({
      ...prev,
      componentInfo: { ...prev.componentInfo, tcAssociatedProductNo: productNumber },
    }));
    toggleDrawer();
  };

  const handleContinue = () => {
    if (!components || components.length === 0) {
      notify("At least one component is required. Please add a component before continuing.", "error");
      return;
    }
    if (onContinue) {
      onContinue(components);
    }
  };

  const onConfirmDeleteComponent = async () => {
    const row = pendingDeleteRow;
    if (!row) {
      setDeleteDialogOpen(false);
      setPendingDeleteRow(null);
      return;
    }

    // Close the delete popup immediately; keep delete running in background
    setPendingDeleteRow(null);
    setDeleteDialogOpen(false);

    setLoadingState(true);
    try {
      const res = await executeDelete(TCAppConfig.TC_DELETE_API, {
        pageName: DELETECONFIG.component,
        tcId,
        componentId: row.componentId,
        shipmentId: row.shipmentId,
        productId: row.productId,
        componentNo: encodeURIComponent(encodeURIComponent(row.componentNumber ?? "")),
      });
      if (res?.status === 200) {
        notify("Successfully deleted", "success");
        await fetchComponentList();
        await onComponentSaved?.();
      } else {
        notify("Failed to delete", "error");
      }
    } catch (err) {
      console.error("Delete component failed:", err);
      notify("Failed to delete", "error");
    } finally {
      setLoadingState(false);
      setPendingDeleteRow(null);
      setDeleteDialogOpen(false);
    }
  };

  const onCancelDeleteComponent = () => {
    setPendingDeleteRow(null);
    setDeleteDialogOpen(false);
  };

  const columns: MRT_ColumnDef<ComponentData>[] = [
    {
      accessorKey: "componentNumber",
      header: "Component Number",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "componentDetail",
      header: "Component Detail",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "componentNetShippingWeight",
      header: "Component Net Shipping Weight (kg)",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "componentSupplementaryWeight",
      header: "Component Supplementary Weight (kg)",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "componentCertifiedWeight",
      header: "Component Certified Weight (kg)",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "componentCertifiedAndNonCertifiedWeight",
      header: "Component Non-Certified Weight (kg)",
      minSize: 60,
      maxSize: 180,
      Cell: ({ row }) =>
        row.original.componentCertifiedAndNonCertifiedWeight != null
          ? row.original.componentCertifiedAndNonCertifiedWeight
          : "-",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
            Component Creation Workspace for
          </Typography>
          <Box sx={{ display: "flex", gap: 4, mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Shipment Number: {shipmentNumber}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Product Number: {productNumber}
            </Typography>
          </Box>
        </Box>
        {components.length === 0 && (
          <IconButton
            onClick={async () => {
              try {
                if (!tcId || !propProductId || !propShipmentId || !shipmentNumber || !productNumber) {
                  onClose();
                  return;
                }
                const params = {
                  pageName: "Product",
                  tcId,
                  productId: propProductId,
                  shipmentId: propShipmentId,
                  shipmentNo: shipmentNumber,
                  productNo: productNumber,
                };
                const result: any = await executeDelete(
                  TCAppConfig.TC_DELETE_API,
                  params,
                  DELETECONFIG
                );
                if (result?.status === 200) {
                  notify("Product deleted successfully", "success");
                  // Inform parent so it can reset productId/snapshot state
                  if (onProductDeleted) {
                    onProductDeleted();
                  }
                } else {
                  notify(
                    result?.data?.message ??
                      "Failed to delete product",
                    "error"
                  );
                }
              } catch (err: any) {
                console.error("Product delete failed:", err);
                notify(
                  err?.response?.data?.message ??
                    err?.message ??
                    "Failed to delete product",
                  "error"
                );
              } finally {
                onClose();
              }
            }}
            sx={{ p: 0 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {loading ? (
        <DialogContent sx={{ backgroundColor: "#ffffff" }}>
          <LoaderComponent />
        </DialogContent>
      ) : (
      <DialogContent sx={{ backgroundColor: "#ffffff" }}>
        <Box sx={{ mb: 4, maxWidth: 420 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, fontSize: "1.4rem" }}
          >
            Product Total Derived From Components
          </Typography>

          <CustomTable
            columns={[
              {
                accessorKey: "weightType",
                header: "Weight Type",
                minSize: 30,
                maxSize: 60,
              },
              {
                accessorKey: "weight",
                header: "Weight (kg)",
                minSize: 20,
                maxSize: 40,
                Cell: ({ row }: any) =>
                  Number(row?.original?.weight ?? 0) || 0,
              },
            ]}
            data={[
              {
                weightType: "Net Shipping Weight",
                weight: totalNetShippingWeight,
              },
              {
                weightType: "Supplementary Weight",
                weight: totalSupplementaryWeight,
              },
              {
                weightType: "Certified Weight",
                weight: totalCertifiedWeight,
              },
              {
                weightType: "Non-Certified Weight",
                weight: totalNonCertifiedWeight,
              },
            ]}
            enableRowActions={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableSorting={false}
          />
        </Box>

        {/* Add Components Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
              {!isViewMode && (
              <StyledButton
                        variant="contained"
                        sx={{ boxShadow: "none" }}
                        onClick={handleAddComponent}
                      >
                        Add Components
                      </StyledButton>
              )}
        </Box>

        {/* Components Table using CustomTable */}
        <Box sx={{ mb: 3 }}>
          <CustomTable
            columns={columns}
            data={components}
            rowCount={rowCount}
            paginations={pagination}
            setPaginations={setPagination}
            enableRowActions={true}
            enableTopToolbar={false}
            enableBottomToolbar={true}
            renderRowActions={({ row }) => (
              <ActionsMenu
                row={row}
                onEdit={(r: any) => handleEditComponent(r.original)}
                onDelete={isViewMode ? undefined : (r: any) => {
                  setPendingDeleteRow(r.original);
                  setDeleteDialogOpen(true);
                }}
              />
            )}
          />
        </Box>

        {/* Bottom Actions */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mb: 2 }}>
          <StyledButton
            variant="contained"
            onClick={handleContinue}
            sx={{ boxShadow: "none" }}
          >
            {editContext ? "Continue to Component Information" : "Continue to Product Information"}
          </StyledButton>
        </Box>
      </DialogContent>
      )}

      {/* Stepper Modal for Adding Components */}
      <ActionsModalPopup
        open={drawerOpen}
        onClose={toggleDrawer}
        title={editingComponentIndex !== null ? "Edit Component" : "Add Component"}
        actions={stepperActions}
      >
        <CustomStepper
          steps={steps}
          activeStep={activeStep}
          onStepChange={(step) => setActiveStep(step)}
        />
      </ActionsModalPopup>

      <AlertPopup
        open={deleteDialogOpen}
        title="Confirm Delete ?"
        message={
          pendingDeleteRow
            ? `Deleting Component ${pendingDeleteRow.componentNumber} will remove all the Associated Records, are you sure want to delete it`
            : ""
        }
        onClose={onCancelDeleteComponent}
        onConfirm={onConfirmDeleteComponent}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Dialog>
  );
};

export default ComponentCreationModal;
